/**
 * Taking the picture of the page, and where it goes (FR-21).
 *
 * One function that turns the page behind the panel into PNG bytes, and the
 * path those bytes are posted at. Nothing here fetches: the panel's `client.ts`
 * is the one layer that talks to the network, and a second one in this file
 * would prefix the base address twice and hold a token that never refreshes.
 * See `screenshotPath` at the bottom for the client method this expects.
 *
 * Nothing here decides what the file is called either. The server builds the
 * name out of the domain (register item D15 rule 2), so the request body is the
 * raw bytes: no multipart part and no filename field for a name to travel in.
 *
 * **What rasterises the page.** `html2canvas` walks the DOM and paints it onto
 * a canvas. It sometimes draws complicated CSS wrongly, and that is the price:
 * the alternative is the screen capture API, which asks the person for
 * permission every single time and records the whole screen rather than the
 * page. For a report about a user interface a redrawn page is enough, and one
 * click is the point. Every application that takes this module on carries that
 * dependency, so it belongs in the adoption runbook and in the panel's own
 * package, never hidden in here.
 *
 * It arrives as a parameter rather than as an import for two reasons. The
 * useful one: a function that takes its rasteriser can be tested without a
 * browser and without the library. The honest one: this file was written on a
 * branch where the panel and its package.json did not exist yet, and importing
 * a package nothing had installed would have been a line that compiled against
 * nothing.
 *
 * **And that seam is not used yet.** There is no vitest on the branch this was
 * written on, so the scale loop, the null from `toBlob` and the answer of null
 * when nothing fits are typechecked and not exercised. The report for this task
 * names it; whoever lands this beside the panel writes those three.
 *
 * **The cap is the server's and this is the courtesy.** The module refuses
 * anything over MAX_SCREENSHOT_BYTES before a byte reaches Graph, which is
 * where a cap has to be. Rendering at a smaller scale here is so that a person
 * on a wide screen at twice the device resolution is not told his screenshot
 * was too large after waiting for it to upload.
 */

/** What a rasteriser has to be able to do. `html2canvas` is one. */
export type Rasteriser = (
  element: HTMLElement,
  options: RasteriserOptions,
) => Promise<HTMLCanvasElement>;

export interface RasteriserOptions {
  /** Multiplier on the CSS pixel size. 1 is the page at its own size. */
  scale: number;
  /** Answer true for an element that must be left out of the picture. */
  ignoreElements: (element: Element) => boolean;
}

/**
 * The largest image the server accepts, from
 * api/feedback/services/attachments.py. It is repeated rather than fetched
 * because a value that has to be there before the first request cannot come
 * from a request. The server is the one that counts: this number being stale
 * costs a person one refusal with a clear message, and it never lets a larger
 * image through.
 */
export const MAX_SCREENSHOT_BYTES = 4 * 1024 * 1024;

/** Tried in order until one fits. Full size first, so most pages are sharp. */
export const SCALES = [2, 1, 0.75, 0.5];

export interface CaptureRequest {
  /** What paints the page. The panel passes `html2canvas`. */
  rasterise: Rasteriser;
  /** What to photograph. The document body unless a host says otherwise. */
  page: HTMLElement;
  /**
   * The panel itself, and anything else that is part of reporting rather than
   * part of the page. A picture of the report form on top of the screen it is
   * about is a picture of the wrong thing.
   */
  hide?: Element[];
  maxBytes?: number;
  scales?: number[];
}

/**
 * The page as PNG bytes, at the largest scale that fits under the cap.
 *
 * Returns null when even the smallest scale is too large. The caller sends the
 * submission anyway: a report without a picture is a report, and refusing to
 * send one because the picture would not fit turns a nicety into a blocker.
 */
export async function captureScreenshot(
  request: CaptureRequest,
): Promise<Blob | null> {
  const hide = request.hide ?? [];
  const maxBytes = request.maxBytes ?? MAX_SCREENSHOT_BYTES;
  const scales = request.scales ?? SCALES;

  for (const scale of scales) {
    const canvas = await request.rasterise(request.page, {
      scale,
      ignoreElements: (element) =>
        hide.some((hidden) => hidden === element || hidden.contains(element)),
    });
    const image = await toPng(canvas);
    if (image !== null && image.size <= maxBytes) {
      return image;
    }
  }
  return null;
}

/** The canvas as a PNG blob, or null when the browser will not give one. */
function toPng(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
}

/**
 * The content type on the request, and what it is worth.
 *
 * The server reads the signature at the front of the bytes and decides for
 * itself what the file is, so this settles nothing at the other end. It is here
 * because a proxy in between reads it, and because sending JSON's content type
 * over a raster would be a lie that costs somebody an afternoon.
 */
export const SCREENSHOT_CONTENT_TYPE = "image/png";

/**
 * Where the image goes, relative to wherever the host mounted the module.
 *
 * A path and not a fetch, and the reason is the panel's own architecture:
 * `client.ts` is the one layer that calls the network, it already holds the
 * base address and asks the host for a token per call so a refresh reaches it,
 * and no component fetches (coding standards 11.10). A send function here would
 * either duplicate that layer or prefix the base address a second time.
 *
 * So the client gets one more method, and this is the only part of it that
 * belongs to the screenshot rather than to the client:
 *
 * ```ts
 * async screenshot(submissionId: number, image: Blob): Promise<StoredScreenshot> {
 *   const token = await this.getToken();
 *   const response = await this.doFetch(`${this.apiBase}${screenshotPath(submissionId)}`, {
 *     method: "POST",
 *     headers: { Authorization: `Bearer ${token}`, "Content-Type": SCREENSHOT_CONTENT_TYPE },
 *     body: image,          // raw bytes, never JSON and never a form
 *   });
 *   // then the same envelope handling every other call in that file already does
 * }
 * ```
 *
 * A failure of that call is not a failure of the report. The submission is
 * written and the reader has been told so by the time the image is sent; a
 * refusal here means his words arrived and his picture did not, and the panel
 * should say nothing louder than that.
 */
export function screenshotPath(submissionId: number): string {
  return `/submissions/${submissionId}/screenshot`;
}
